import React, { lazy, Suspense, useContext, useEffect, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { motion, useReducedMotion } from 'framer-motion';
import { SkillLaunchContext } from './SkillLaunchContext';
import {
  ArrowDownTrayIcon,
  ArrowRightIcon,
  ChatBubbleLeftRightIcon,
  ArrowPathIcon,
  CalendarDaysIcon,
  CheckIcon,
  FolderIcon,
  StarIcon,
  TagIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { i18nService } from '../../services/i18n';
import { OnlineSkill, Skill } from '../../types/skill';
import { formatLocalizedCompactNumber } from '../../utils/localizedNumber';
import SkillAvatar from './SkillAvatar';

// 详情打开后再载入完整阅读器；列表与服务端静态渲染不应加载公式字体及高亮资源。
const MarkdownContent = lazy(() => import('../MarkdownContent'));

type InstallStatus = 'idle' | 'installing' | 'installed';

export type SkillDetailItem =
  | {
    kind: 'local';
    skill: Skill;
    description: string;
    categoryLabel?: string;
    onToggle: (skillId: string) => void;
  }
  | {
    kind: 'online';
    skill: OnlineSkill;
    summary: string;
    tagLabel?: string;
    installStatus: InstallStatus;
    onInstall: (slug: string) => void;
  };

interface SkillDetailDialogProps {
  item: SkillDetailItem | null;
  onClose: () => void;
}

const localeMap: Record<string, string> = {
  zh: 'zh-CN',
  'zh-TW': 'zh-TW',
  en: 'en-US',
  ja: 'ja-JP',
  ko: 'ko-KR',
  ru: 'ru-RU',
  hi: 'hi-IN',
};

/** 将技能更新时间格式化为当前语言；无效时间保留原值以便识别数据问题。 */
const formatDate = (value: number | string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(localeMap[i18nService.getLanguage()] ?? 'en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
};

/** 技能说明、示例提问与使用入口共用详情弹窗，安装/启停仍由原页面负责。 */
const SkillDetailDialog: React.FC<SkillDetailDialogProps> = ({ item, onClose }) => {
  const launchContext = useContext(SkillLaunchContext);
  const reducedMotion = useReducedMotion();
  const [launchError, setLaunchError] = useState(false);
  useEffect(() => setLaunchError(false), [item?.skill]);

  if (!item || typeof document === 'undefined') return null;

  const isLocal = item.kind === 'local';
  const name = isLocal ? item.skill.name : item.skill.displayName;
  const description = isLocal ? item.description : item.skill.description || item.summary;
  const category = isLocal ? item.categoryLabel : item.tagLabel;
  const updatedAt = item.skill.updatedAt;

  const skillId = isLocal ? item.skill.id : item.skill.slug;
  const availableSkill = launchContext?.skills.find(skill => skill.id === skillId && skill.enabled);
  /** 业务层再次验证后才能关闭弹窗；已删除/停用或换账号时保留失败提示。 */
  const launch = (question?: string) => {
    if (availableSkill && launchContext?.launch(availableSkill.id, question)) onClose();
    else setLaunchError(true);
  };
  const fallbackQuestions = [1, 2, 3].map(index => i18nService.t(`skillsFeaturedDetailFallbackQuestion${index}`).replace('{name}', name));

  const questions = item.skill.suggestedQuestions?.length ? item.skill.suggestedQuestions.slice(0, 4) : fallbackQuestions;
  const artifacts = item.skill.outputArtifacts ?? [];

  return (
    <Dialog open onClose={onClose} className="relative z-[200]">
      <motion.div
        initial={reducedMotion ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: reducedMotion ? 0 : 0.16, ease: 'easeOut' }}
        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/35 p-5 backdrop-blur-[2px]"
      >
        <Dialog.Panel as={motion.div}
          initial={reducedMotion ? false : { opacity: 0, scale: 0.965, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: reducedMotion ? 0 : 0.22, ease: [0.22, 1, 0.36, 1] }}
          className="relative flex max-h-[90dvh] w-full max-w-[600px] flex-col overflow-hidden rounded-2xl border border-claude-border bg-white shadow-[0_24px_70px_rgba(0,0,0,0.22)] dark:border-claude-darkBorder dark:bg-claude-darkSurface"
        >
          <div className="flex shrink-0 items-start gap-3.5 border-b border-claude-borderLight px-6 pb-5 pt-6 pr-16 dark:border-claude-darkBorderLight">
            <SkillAvatar name={name} className="h-12 w-12 rounded-xl [&_svg]:h-5 [&_svg]:w-5" />
            <div className="min-w-0 flex-1">
              <Dialog.Title className="truncate text-lg font-semibold text-claude-text dark:text-claude-darkText">
                {name}
              </Dialog.Title>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {isLocal ? (
                  <>
                    <span className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${item.skill.enabled
                      ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                      : 'bg-claude-surfaceMuted text-claude-textSecondary dark:bg-claude-darkSurfaceHover dark:text-claude-darkTextSecondary'
                    }`}>
                      {i18nService.t(item.skill.enabled ? 'enabled' : 'disabled')}
                    </span>
                    {item.skill.isOfficial && (
                      <span className="rounded-md bg-claude-surfaceMuted px-2 py-0.5 text-[11px] font-medium text-claude-textSecondary dark:bg-claude-darkSurfaceHover dark:text-claude-darkTextSecondary">
                        {i18nService.t('official')}
                      </span>
                    )}
                    {item.skill.isBuiltIn && (
                      <span className="rounded-md bg-claude-surfaceMuted px-2 py-0.5 text-[11px] font-medium text-claude-textSecondary dark:bg-claude-darkSurfaceHover dark:text-claude-darkTextSecondary">
                        {i18nService.t('skillDetailsBuiltIn')}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="rounded-md bg-claude-surfaceMuted px-2 py-0.5 text-[11px] font-medium text-claude-textSecondary dark:bg-claude-darkSurfaceHover dark:text-claude-darkTextSecondary">
                    v{item.skill.version}
                  </span>
                )}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="absolute right-5 top-5 flex h-8 w-8 items-center justify-center rounded-lg text-claude-textSecondary transition-colors hover:bg-claude-surfaceHover hover:text-claude-text dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover dark:hover:text-claude-darkText"
            title={i18nService.t('close')}
            aria-label={i18nService.t('close')}
          >
            <XMarkIcon className="h-4.5 w-4.5" />
          </button>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            <section>
              <h3 className="text-xs font-semibold uppercase text-claude-textSecondary dark:text-claude-darkTextSecondary">
                {i18nService.t('skillDetailsDescription')}
              </h3>
              <div className="mt-2 text-sm leading-6 text-claude-text dark:text-claude-darkText"><Suspense fallback={<p className="whitespace-pre-wrap">{description}</p>}><MarkdownContent content={description} /></Suspense></div>
            </section>

            {artifacts.length > 0 && <section className="mt-5" aria-label={i18nService.t('skillsFeaturedDetailOutputArtifacts')}>
              <h3 className="text-sm font-medium">{i18nService.t('skillsFeaturedDetailOutputArtifacts')}</h3>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-6 text-claude-textSecondary dark:text-claude-darkTextSecondary">
                {artifacts.map(artifact => <li key={artifact} className="break-words">{artifact}</li>)}
              </ul>
            </section>}

            <section className="mt-5 space-y-2" aria-label={i18nService.t('skillsFeaturedDetailTryAsking')}>
              <h3 className="flex items-center gap-2 text-sm font-medium"><ChatBubbleLeftRightIcon className="h-4 w-4" />{i18nService.t('skillsFeaturedDetailTryAsking')}</h3>
              <p className="text-xs leading-5 text-claude-textSecondary dark:text-claude-darkTextSecondary">{i18nService.t(availableSkill ? 'skillsFeaturedDetailDraftHint' : 'skillsFeaturedDetailPreviewHint')}</p>
              {questions.map(question => <button key={question} type="button" disabled={!availableSkill} onClick={() => launch(question)} className="flex w-full items-center gap-2 rounded-xl border border-claude-border dark:border-claude-darkBorder px-3 py-2 text-left text-sm transition-colors motion-reduce:transition-none enabled:hover:bg-claude-accent/5 disabled:cursor-default disabled:opacity-70 focus-visible:ring-2 focus-visible:ring-claude-accent">
                <span className="min-w-0 flex-1 break-words">{question}</span>{availableSkill && <ArrowRightIcon className="h-4 w-4 shrink-0 opacity-60" />}
              </button>)}
            </section>
            {launchError && <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">{i18nService.t('skillLaunchUnavailable')}</p>}
            <dl className="mt-5 grid grid-cols-1 border-y border-claude-borderLight sm:grid-cols-2 dark:border-claude-darkBorderLight">
              <div className="flex min-w-0 items-center gap-3 py-3 sm:pr-4">
                <TagIcon className="h-4 w-4 shrink-0 text-claude-textSecondary dark:text-claude-darkTextSecondary" />
                <div className="min-w-0">
                  <dt className="text-[11px] text-claude-textSecondary dark:text-claude-darkTextSecondary">
                    {i18nService.t('skillDetailsCategory')}
                  </dt>
                  <dd className="mt-0.5 truncate text-xs font-medium text-claude-text dark:text-claude-darkText">
                    {category || i18nService.t('uncategorized')}
                  </dd>
                </div>
              </div>
              <div className="flex min-w-0 items-center gap-3 border-t border-claude-borderLight py-3 sm:border-l sm:border-t-0 sm:pl-4 dark:border-claude-darkBorderLight">
                <CalendarDaysIcon className="h-4 w-4 shrink-0 text-claude-textSecondary dark:text-claude-darkTextSecondary" />
                <div className="min-w-0">
                  <dt className="text-[11px] text-claude-textSecondary dark:text-claude-darkTextSecondary">
                    {i18nService.t('skillDetailsUpdated')}
                  </dt>
                  <dd className="mt-0.5 truncate text-xs font-medium text-claude-text dark:text-claude-darkText">
                    {formatDate(updatedAt)}
                  </dd>
                </div>
              </div>
              {!isLocal && (
                <>
                  <div className="flex min-w-0 items-center gap-3 border-t border-claude-borderLight py-3 sm:pr-4 dark:border-claude-darkBorderLight">
                    <ArrowDownTrayIcon className="h-4 w-4 shrink-0 text-claude-textSecondary dark:text-claude-darkTextSecondary" />
                    <div className="min-w-0">
                      <dt className="text-[11px] text-claude-textSecondary dark:text-claude-darkTextSecondary">
                        {i18nService.t('skillsOnlineSortDownloads')}
                      </dt>
                      <dd className="mt-0.5 text-xs font-medium tabular-nums text-claude-text dark:text-claude-darkText">
                        {formatLocalizedCompactNumber(item.skill.downloads, i18nService.getLanguage())}
                      </dd>
                    </div>
                  </div>
                  <div className="flex min-w-0 items-center gap-3 border-t border-claude-borderLight py-3 sm:border-l sm:pl-4 dark:border-claude-darkBorderLight">
                    <StarIcon className="h-4 w-4 shrink-0 text-claude-textSecondary dark:text-claude-darkTextSecondary" />
                    <div className="min-w-0">
                      <dt className="text-[11px] text-claude-textSecondary dark:text-claude-darkTextSecondary">
                        {i18nService.t('skillsOnlineSortStars')}
                      </dt>
                      <dd className="mt-0.5 text-xs font-medium tabular-nums text-claude-text dark:text-claude-darkText">
                        {formatLocalizedCompactNumber(item.skill.stars, i18nService.getLanguage())}
                      </dd>
                    </div>
                  </div>
                </>
              )}
            </dl>

            {isLocal && item.skill.skillPath && (
              <div className="mt-4 flex items-start gap-2.5 text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">
                <FolderIcon className="mt-0.5 h-4 w-4 shrink-0" />
                <div className="min-w-0">
                  <div className="text-[11px]">{i18nService.t('skillDetailsLocation')}</div>
                  <div className="mt-1 break-all font-mono text-[11px] leading-5">{item.skill.skillPath}</div>
                </div>
              </div>
            )}
          </div>

          <div className="flex min-h-16 shrink-0 flex-wrap items-center justify-between gap-4 border-t border-claude-borderLight bg-claude-surfaceMuted/45 px-6 py-3 dark:border-claude-darkBorderLight dark:bg-claude-darkSurfaceHover/25">
            <span className="text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">
              {isLocal ? i18nService.t('skillDetailsLocal') : i18nService.t('skillsOnlineTab')}
            </span>
            <div className="flex items-center gap-3">
              {availableSkill && <button type="button" onClick={() => launch()} className="inline-flex items-center gap-2 rounded-lg bg-claude-accent px-3 py-2 text-sm text-white hover:opacity-90">{i18nService.t('skillsFeaturedDetailUseSkill')}<ArrowRightIcon className="h-4 w-4" /></button>}
              {isLocal ? (
                <button
                  type="button"
                  role="switch"
                  aria-checked={item.skill.enabled}
                  onClick={() => item.onToggle(item.skill.id)}
                  className={`flex h-6 w-11 items-center rounded-full transition-colors ${item.skill.enabled
                    ? 'bg-claude-accent'
                    : 'bg-claude-border dark:bg-claude-darkBorder'
                  }`}
                  aria-label={`${name} · ${i18nService.t(item.skill.enabled ? 'enabled' : 'disabled')}`}
                >
                  <span className={`h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${item.skill.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => item.onInstall(item.skill.slug)}
                  disabled={item.installStatus !== 'idle'}
                  className={`inline-flex h-9 items-center gap-2 rounded-lg border px-3 text-xs font-medium transition-colors ${item.installStatus === 'idle'
                    ? 'border-claude-border bg-white text-claude-text hover:bg-claude-surfaceHover dark:border-claude-darkBorder dark:bg-claude-darkSurface dark:text-claude-darkText dark:hover:bg-claude-darkSurfaceHover'
                    : 'cursor-default border-transparent text-claude-textSecondary dark:text-claude-darkTextSecondary'
                  }`}
                >
                  {item.installStatus === 'installed' ? (
                    <CheckIcon className="h-4 w-4" />
                  ) : item.installStatus === 'installing' ? (
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  ) : (
                    <ArrowDownTrayIcon className="h-4 w-4" />
                  )}
                  {i18nService.t(item.installStatus === 'installed'
                    ? 'skillsOnlineInstalled'
                    : item.installStatus === 'installing'
                      ? 'skillsOnlineInstalling'
                      : 'skillsOnlineInstall')}
                </button>
              )}
            </div>
          </div>
        </Dialog.Panel>
      </motion.div>
    </Dialog>
  );
};

export default SkillDetailDialog;
